import { deleteWhere, findAllEntriesInTable } from 'store/functions/inputFunctions.js';
import { deleteRowGeoJsonHelper } from 'store/functions/geoJsonFunctions.js';
import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';
import { validateTable } from './validateRow.js';

/**
 * What a person is told when deleting a vehicle type took its travel costs with
 * it. An emptied matrix is the serious one: nothing can be routed over it, and
 * a run would stop.
 */
export const vehicleTypeCostRowsRemovedMessage = (removed, remaining) => {
  const rows = `${removed} travel cost ${removed === 1 ? 'row' : 'rows'}`;
  if (remaining === 0) {
    return `Deleting that vehicle type also removed the ${rows} that belonged to it, which was all of them — your travel-cost table is now empty. Add a vehicle type and populate travel costs again before you run.`;
  }
  return `Deleting that vehicle type also removed the ${rows} that belonged to it. Your other vehicle types' travel costs are unchanged.`;
};

export const deleteRowHelper = (state, tableName, uuid) => {
  let newData = {...state.data}
  const idField = STATIC_TABLE_SETTINGS[tableName]['idField'] || 'UUID';
  newData = deleteWhere(newData, tableName, idField, uuid)

  if (tableName === 'locations') {
    newData = deleteWhere(newData, 'trips', 'fromLocationId', uuid);
    newData = deleteWhere(newData, 'trips', 'toLocationId', uuid);
    newData = deleteWhere(newData, 'travel_costs', 'toLocationId', uuid);
    newData = deleteWhere(newData, 'travel_costs', 'fromLocationId', uuid);
  }

  /*
  A deleted vehicle type takes its travel-cost rows with it, exactly as a
  deleted location does above.

  They cannot be kept. The generic pick-list sweep below only nulls the row's
  vehicleType NAME, leaving the id of a type that no longer exists: rows nothing
  will ever read, which still count as "typed" and so decide how every row added
  afterwards is written. Nulling the id instead would be worse — that turns them
  into untyped rows inside a typed matrix, which is the shape the solver refuses.
  */
  if (tableName === 'vehicle_types') {
    const before = Array.isArray(newData['travel_costs']) ? newData['travel_costs'].length : 0;
    newData = deleteWhere(newData, 'travel_costs', 'vehicleTypeId', uuid);
    const removed = before - newData['travel_costs'].length;
    // Rows disappearing is not something anyone asked for when they deleted a
    // vehicle type, so it is said plainly — especially when that was the last
    // type and the matrix is now empty, which stops a run outright.
    if (removed > 0 && state.notifications && Array.isArray(state.notifications.routine)) {
      state.notifications.routine.push({
        type: newData['travel_costs'].length === 0 ? 'warning' : 'info',
        message: vehicleTypeCostRowsRemovedMessage(removed, newData['travel_costs'].length),
      });
    }
  }

  //checks to see if any dropdown pick-lists will be impacted by this change.
  const updatedTables = new Set();
  for (const [iterTableName] of Object.entries(newData)) {
    if(iterTableName === tableName) {continue}
    if (!STATIC_TABLE_SETTINGS[iterTableName]) {continue}
    const dropdownSources = STATIC_TABLE_SETTINGS[iterTableName]['dropdownSource']
    if (!dropdownSources) {continue}
    for (const [iterColumnName, dropdownSource] of Object.entries(dropdownSources)) {
      if (dropdownSource) {
        if (dropdownSource.tableName === tableName) {
          const dropdownColumn = dropdownSource['column']
          newData[iterTableName].forEach((elem, idx) => {
            const columnVal = elem[iterColumnName];
            const matchIdcs = findAllEntriesInTable(newData, tableName, [dropdownColumn], [columnVal])
            if (matchIdcs.length === 0) {
              newData[iterTableName][idx][iterColumnName] = null;
              updatedTables.add(iterTableName)
            }
          })

        }
      }
    }
  }
  
  let newState = {...state};
  newState['data'] = newData;
  for (const updatedTableName of updatedTables) {
    newState = validateTable(newState, updatedTableName)
  }

  newState['geoJson'] = deleteRowGeoJsonHelper(newState['geoJson'], uuid, tableName);

  return newState
}
